import { useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import {
  FindManyClinicWidgetRequestTypeQuery,
  SortOrder,
  TaskStatus,
  TaskType,
  useClinicHasAutomationsThatCreateTasksQuery,
  useFindManyClinicWidgetRequestTypeQuery,
  useFindManyTaskLazyQuery,
  WidgetRequestType,
} from 'shared/types/graphql';

type WidgetRequestsWithPendingTasks = NonNullable<
  FindManyClinicWidgetRequestTypeQuery['findManyClinicWidgetRequestType'][0]
> & {
  hasPendingTasks?: boolean;
};

type UseTaskListOptionsReturnType = {
  clinicAppointmentTypes?: WidgetRequestsWithPendingTasks[];
  otherTypes: { taskType: TaskType; displayName: string }[];
  loaded: boolean;
};

const useTaskListOptions = (clinicId: string): UseTaskListOptionsReturnType => {
  const [clinicAppointmentTypes, setClinicAppointmentTypes] = useState<WidgetRequestsWithPendingTasks[] | undefined>();
  const [includeAutomations, setIncludeAutomations] = useState<boolean | undefined>();
  const { data: _clinicAppointmentTypes } = useFindManyClinicWidgetRequestTypeQuery({
    variables: {
      where: {
        clinicWidgetSetting: {
          clinic: {
            some: {
              id: {
                equals: clinicId,
              },
            },
          },
        },
        requestType: {
          notIn: [
            WidgetRequestType.TalkToStaff,
            WidgetRequestType.InboundSms,
            WidgetRequestType.DirectBooking,
            WidgetRequestType.Teletriage,
          ],
        },
      },
      orderBy: [
        {
          isActive: SortOrder.Desc,
        },
        {
          isDeleted: SortOrder.Asc,
        },
        {
          createdAt: SortOrder.Asc,
        },
      ],
    },
    skip: !clinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const { data: includeAutomationsData } = useClinicHasAutomationsThatCreateTasksQuery({
    variables: {
      clinicId: clinicId,
    },
    skip: !clinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const [getActiveTasksForInactiveOrDeletedWidgetRequests] = useFindManyTaskLazyQuery();
  const [getActiveTasksForInactiveOrDeletedAutomations] = useFindManyTaskLazyQuery();

  useEffect(() => {
    if (_clinicAppointmentTypes) {
      const apptTypes = _clinicAppointmentTypes.findManyClinicWidgetRequestType;
      const inactiveOrDeleted = apptTypes.filter(
        ({ isActive, isDeleted, shouldCreateTask }) => !isActive || isDeleted || !shouldCreateTask,
      );

      if (inactiveOrDeleted.length > 0) {
        getActiveTasksForInactiveOrDeletedWidgetRequests({
          variables: {
            where: {
              clinicId: {
                equals: clinicId,
              },
              status: {
                in: [TaskStatus.Open, TaskStatus.InProgress],
              },
              type: { equals: TaskType.WidgetRequest },
              widgetRequest: {
                clinicWidgetRequestType: {
                  id: { in: inactiveOrDeleted.map(({ id }) => id) },
                },
              },
            },
          },
        }).then((res) => {
          const tasks = res?.data?.findManyTask || [];
          const updatedApptTypes = apptTypes.map((apptType) =>
            tasks.some((task) => task?.widgetRequest?.clinicWidgetRequestType?.id === apptType.id)
              ? { ...apptType, hasPendingTasks: true }
              : apptType,
          );
          setClinicAppointmentTypes(updatedApptTypes);
        });
      } else {
        setClinicAppointmentTypes(apptTypes);
      }
    }
  }, [_clinicAppointmentTypes, clinicId, getActiveTasksForInactiveOrDeletedWidgetRequests]);

  useEffect(() => {
    if (includeAutomationsData?.clinicHasAutomationsThatCreateTasks === undefined) {
      return;
    }

    if (includeAutomationsData?.clinicHasAutomationsThatCreateTasks === false) {
      getActiveTasksForInactiveOrDeletedAutomations({
        variables: {
          where: {
            clinicId: {
              equals: clinicId,
            },
            status: {
              in: [TaskStatus.Open, TaskStatus.InProgress],
            },
            type: { equals: TaskType.Automation },
          },
          take: 1,
        },
      }).then((res) => {
        if (res?.data?.findManyTask?.length) {
          setIncludeAutomations(true);
        } else {
          setIncludeAutomations(false);
        }
      });
    } else {
      setIncludeAutomations(true);
    }
  }, [includeAutomationsData, clinicId, getActiveTasksForInactiveOrDeletedAutomations]);

  const otherTypes = useMemo(
    () => [
      {
        taskType: TaskType.Custom,
        displayName: 'Custom Tasks',
      },
      ...(includeAutomations
        ? [
            {
              taskType: TaskType.Automation,
              displayName: 'Automations',
            },
          ]
        : []),
    ],
    [includeAutomations],
  );

  const loaded = useMemo(
    () => clinicAppointmentTypes !== undefined && includeAutomations !== undefined,
    [clinicAppointmentTypes, includeAutomations],
  );

  return {
    clinicAppointmentTypes,
    otherTypes,
    loaded,
  };
};

export default useTaskListOptions;
